import React, { useState, useContext, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { useLocation } from "react-router-dom";
import Select from "react-select";

// 은행 목록 데이터
const bankOptions = [
  { value: "KB", label: "KB국민은행", icon: "/bank-icons/kb.png" },
  { value: "shinhan", label: "신한은행", icon: "/bank-icons/shinhan.png" },
  { value: "woori", label: "우리은행", icon: "/bank-icons/woori.png" },
  { value: "smk", label: "새마을금고", icon: "/bank-icons/smk.png" },
  { value: "hana", label: "하나은행", icon: "/bank-icons/hana.png" },
  { value: "nh", label: "농협은행", icon: "/bank-icons/nh.png" },
  { value: "sh", label: "신협은행", icon: "/bank-icons/sh.png" },
  { value: "ibk", label: "기업은행", icon: "/bank-icons/ibk.png" },
  { value: "sc", label: "SC제일은행", icon: "/bank-icons/sc.png" },
  { value: "kakao", label: "카카오뱅크", icon: "/bank-icons/kakao.png" },
  { value: "toss", label: "토스뱅크", icon: "/bank-icons/toss.png" },
  { value: "kdb", label: "산업은행", icon: "/bank-icons/kdb.png" },
  { value: "city", label: "한국씨티은행", icon: "/bank-icons/city.png" },
  { value: "dgb", label: "대구은행", icon: "/bank-icons/dgb.png" },
  { value: "bnk", label: "부산은행", icon: "/bank-icons/bnk.png" },
  { value: "kjb", label: "광주은행", icon: "/bank-icons/kjb.png" },
  { value: "knb", label: "경남은행", icon: "/bank-icons/bnk.png" },
  { value: "kbank", label: "케이뱅크", icon: "/bank-icons/kbank.png" },
  { value: "suhyup", label: "Sh수협은행", icon: "/bank-icons/suhyup.png" },
  { value: "jeju", label: "제주은행", icon: "/bank-icons/jeju.png" },
  { value: "post", label: "우체국", icon: "/bank-icons/post.png" },
];

// 진동 애니메이션
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

// 폼 섹션 애니메이션 variants 추가
const formVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const formContainerVariants = {
  initial: {
    height: "auto",
    opacity: 0,
  },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 },
    },
  },
  exit: {
    height: "auto",
    opacity: 0,
  },
};

const formContentVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  },
  exit: (direction) => ({
    x: direction < 0 ? 500 : -500,
    opacity: 0,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  }),
};

// 커스텀 옵션 컴포넌트
const CustomOption = ({ innerProps, label, data }) => (
  <OptionContainer {...innerProps}>
    <BankIcon src={data.icon} alt={label} />
    <span>{label}</span>
  </OptionContainer>
);

// 커스텀 단일 값 컴포넌트
const CustomSingleValue = ({ children, data }) => (
  <SingleValueContainer>
    <BankIcon src={data.icon} alt={children} />
    <span>{children}</span>
  </SingleValueContainer>
);

// 토스트 애니메이션
const toastVariants = {
  initial: {
    opacity: 0,
    y: -50, // 상단에서 아래로 나타나는 효과
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    y: -50, // 다시 상단으로 사라지는 효과
    transition: {
      duration: 0.3,
    },
  },
};

const UserDataCollection = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState(null);
  const [[step, direction], setStep] = useState([1, 0]); // direction 추가

  const [form, setForm] = useState({
    name: "",
    birth: "",
    email: "",
    address: "",
    detailAddress: "",
    bank: null,
    accountNumber: "",
    accountHolder: "",
    phoneNumber: "",
    telecom: "",
    referralSourceCode: "",
    referralDetailCode: "",
  });

  // 동의 상태 추가
  const [consents, setConsents] = useState({
    victimConfirmation: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [errors, setErrors] = useState({
    birth: "",
    email: "",
    bank: "",
    accountNumber: "",
    accountHolder: "",
    victimConfirmation: "",
  });

  const [shakingFields, setShakingFields] = useState({
    birth: false,
    email: false,
    bank: false,
    accountNumber: false,
    accountHolder: false,
    victimConfirmation: false,
  });

  // 토스트 알림을 위한 상태 추가
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 토스트 메시지 표시 함수
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const nameRef = useRef(null);
  const birthRef = useRef(null);
  const emailRef = useRef(null);
  const addressRef = useRef(null);
  const detailAddressRef = useRef(null);

  // 카카오 주소 API 스크립트 로드
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // URL 파라미터에서 redirect 값 추출
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get("redirect");
    if (redirect) {
      setRedirectPath(`/${redirect}`);
    }
  }, [location]);

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    forceScrollTop();
  }, []);

  // 단계 변경 시 스크롤 초기화
  useEffect(() => {
    forceScrollTop();
  }, [step]);

  // 페이지 로드 시 신청 유형 확인
  useEffect(() => {
    // 신청 유형 쿠키 확인
    const applicationType = Cookies.get("applicationType");

    // 페이지 타이틀 동적 설정
    if (applicationType === "representative") {
      // 대표자 신청일 경우 헤더 텍스트 변경
      if (document.title) {
        document.title = "대표자 신청 - 기본 정보 입력";
      }
    }
  }, []);

  // 신청 유형에 따라 다른 헤더 텍스트 표시
  const applicationType = Cookies.get("applicationType");
  const isRepresentative = applicationType === "representative";

  // 다음 주소 검색 실행 함수
  const openPostcodeSearch = () => {
    // 기존 container가 있다면 제거
    const existingContainer = document.getElementById("postcode-container");
    if (existingContainer) {
      existingContainer.remove();
    }

    // 새 container 생성
    const postcodeContainer = document.createElement("div");
    postcodeContainer.id = "postcode-container";
    postcodeContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    // 내부 컨테이너 생성
    const innerContainer = document.createElement("div");
    innerContainer.style.cssText = `
      position: relative;
      width: 90%;
      max-width: 500px;
      height: 500px;
      background-color: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    // 내부 컨테이너 클릭 시 이벤트 버블링 방지
    innerContainer.onclick = function (e) {
      e.stopPropagation();
    };

    postcodeContainer.appendChild(innerContainer);
    document.body.appendChild(postcodeContainer);

    // 외부 영역 클릭 시 모달 닫기
    postcodeContainer.onclick = function () {
      document.body.removeChild(postcodeContainer);
    };

    new window.daum.Postcode({
      oncomplete: function (data) {
        // 도로명 주소 또는 지번 주소 선택
        let addr = data.roadAddress || data.jibunAddress;

        // 주소 필드 업데이트
        setForm((prev) => ({
          ...prev,
          address: addr,
        }));

        // 상세주소 필드에 포커스
        setTimeout(() => {
          if (detailAddressRef.current) {
            detailAddressRef.current.focus();
          }
        }, 100);

        // 검색 완료 후 컨테이너 제거
        document.body.removeChild(postcodeContainer);
      },
      // 검색창 크기 및 UI 설정
      width: "100%",
      height: "100%",
      maxSuggestItems: 5,
      theme: {
        searchBgColor: "#FFFFFF", // 검색창 배경색을 흰색으로 변경
        queryTextColor: "#333333", // 검색창 텍스트 색상을 어두운 색으로 변경
        emphTextColor: "#0075FF", // 강조 텍스트 색상
        outlineColor: "#e0e0e0", // 테두리 색상
      },
      alwaysShowEngAddr: false,
      hideMapBtn: true,
      hideEngBtn: true,
      shorthand: true,
      animation: true,
    }).embed(innerContainer);
  };

  const handleChange = (key) => (e) => {
    let value = e.target.value;

    // 생년월일은 숫자만 입력 가능하게 처리
    if (key === "birth") {
      value = value.replace(/[^0-9]/g, "").slice(0, 8);

      // 8자리가 다 채워졌을 때 키보드 닫기
      if (value.length === 8) {
        birthRef.current.blur();
      }
    }

    setForm((f) => ({ ...f, [key]: value }));

    // 입력 중에는 에러 메시지 초기화
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  // 생년월일 유효성 검사
  const validateBirth = (birth) => {
    const birthRegex = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;
    if (!birthRegex.test(birth)) {
      return "올바른 생년월일 형식이 아닙니다. (예: 19990315)";
    }

    // 미성년자 확인 (2007년 1월 1일 이후 출생자)
    if (parseInt(birth) >= 20070101) {
      return "미성년자는 신청이 불가합니다. (2007년 1월 1일 이후 출생자)";
    }

    return "";
  };

  // 이메일 유효성 검사
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(email)) {
      return "올바른 이메일 주소 형식이 아닙니다.";
    }
    return "";
  };

  // 계좌번호 유효성 검사
  const validateAccountNumber = (accountNumber) => {
    // 숫자와 하이픈만 허용
    const accountRegex = /^[0-9-]+$/;
    if (!accountRegex.test(accountNumber)) {
      return "계좌번호는 숫자만 입력 가능합니다.";
    }
    return "";
  };

  // 예금주명 유효성 검사
  const validateAccountHolder = (accountHolder) => {
    if (accountHolder.trim().length < 2) {
      return "예금주명을 정확히 입력해주세요.";
    }
    if (accountHolder.trim() !== form.name.trim()) {
      return "예금주명은 입력하신 이름과 동일해야 합니다.";
    }
    return "";
  };

  // 진동 효과 실행 후 초기화
  useEffect(() => {
    // 진동 효과가 있는 필드 확인
    const hasShakingField = Object.values(shakingFields).some((val) => val);

    if (hasShakingField) {
      const timer = setTimeout(() => {
        setShakingFields({
          birth: false,
          email: false,
          bank: false,
          accountNumber: false,
          accountHolder: false,
          victimConfirmation: false,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shakingFields]);

  // 모든 가능한 스크롤 위치를 0으로 강제 설정하는 함수
  const forceScrollTop = () => {
    // 메인 문서 요소들의 스크롤 초기화
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // iOS/Safari 대응
    if (window.pageYOffset !== undefined) {
      window.scrollTo(0, 0);
    }

    // 컨텐츠 래퍼 스크롤 초기화
    const contentWrapper = document.querySelector(".content-wrapper");
    if (contentWrapper) {
      contentWrapper.scrollTop = 0;
    }

    // 추가 스크롤 초기화를 위해 지연 실행
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 50);
  };

  // 첫 번째 단계 완료 처리
  const handleFirstStepComplete = () => {
    if (isFirstStepValid()) {
      // 스크롤 위치를 강제로 맨 위로 이동
      forceScrollTop();

      setStep([2, 1]); // 오른쪽으로 이동
    }
  };

  // 두 번째 단계 완료 처리
  const handleSecondStepComplete = () => {
    if (isSecondStepValid()) {
      // 스크롤 위치를 강제로 맨 위로 이동
      forceScrollTop();

      setStep([3, 1]); // 세 번째 단계로 이동
    }
  };

  // 이전 단계로 이동하는 함수 추가
  const handlePrevStep = () => {
    // 스크롤 위치를 강제로 맨 위로 이동
    forceScrollTop();

    if (step === 2) {
      setStep([1, -1]); // 첫 번째 단계로
    } else if (step === 3) {
      setStep([2, -1]); // 두 번째 단계로
    }
  };

  // 첫 번째 단계 유효성 검사
  const isFirstStepValid = () => {
    const birthError = validateBirth(form.birth);
    const emailError = validateEmail(form.email);
    let isValid = true;

    setErrors({
      birth: birthError,
      email: emailError,
    });

    if (birthError) {
      birthRef.current.focus();
      setShakingFields({ ...shakingFields, birth: true });
      isValid = false;
    } else if (emailError) {
      emailRef.current.focus();
      setShakingFields({ ...shakingFields, email: true });
      isValid = false;
    }

    return isValid;
  };

  // 첫 번째 단계 입력 완료 여부 확인
  const isFirstStepFilled = () => {
    return (
      form.name.trim() !== "" &&
      form.birth.trim() !== "" &&
      form.email.trim() !== "" &&
      form.address.trim() !== ""
    );
  };

  // 두 번째 단계 입력 완료 여부 확인
  const isSecondStepFilled = () => {
    return (
      form.bank !== null &&
      form.accountNumber.trim() !== "" &&
      form.accountHolder.trim() !== ""
    );
  };

  // 두 번째 단계 유효성 검사
  const isSecondStepValid = () => {
    const accountNumberError = validateAccountNumber(form.accountNumber);
    const accountHolderError = validateAccountHolder(form.accountHolder);
    let isValid = true;

    setErrors({
      bank: !form.bank ? "은행을 선택해주세요." : "",
      accountNumber: accountNumberError,
      accountHolder: accountHolderError,
    });

    if (!form.bank) {
      setShakingFields({ ...shakingFields, bank: true });
      isValid = false;
    } else if (accountNumberError) {
      accountNumberRef.current.focus();
      setShakingFields({ ...shakingFields, accountNumber: true });
      isValid = false;
    } else if (accountHolderError) {
      accountHolderRef.current.focus();
      setShakingFields({ ...shakingFields, accountHolder: true });
      isValid = false;
    }

    return isValid;
  };

  // 세 번째 단계 입력 완료 여부 확인
  const isThirdStepFilled = () => {
    return consents.victimConfirmation;
  };

  // 동의 체크박스 변경 핸들러
  const handleConsentChange = (consentType) => {
    setConsents((prev) => ({
      ...prev,
      [consentType]: !prev[consentType],
    }));

    // 에러 메시지 제거
    if (errors[consentType]) {
      setErrors((prev) => ({
        ...prev,
        [consentType]: "",
      }));
    }
  };

  // 세 번째 단계 유효성 검사
  const validateConsents = () => {
    const newErrors = {};
    const newShakingFields = {};

    // 필수 동의 항목 검사
    if (!consents.victimConfirmation) {
      newErrors.victimConfirmation = "유의사항에 동의해주세요.";
      newShakingFields.victimConfirmation = true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    setShakingFields((prev) => ({ ...prev, ...newShakingFields }));

    // 흔들림 효과 제거
    setTimeout(() => {
      setShakingFields((prev) => ({
        ...prev,
        victimConfirmation: false,
      }));
    }, 500);

    return Object.keys(newErrors).length === 0;
  };

  // 최종 제출 처리
  const handleSubmit = async () => {
    if (!isThirdStepFilled() || !validateConsents()) {
      return;
    }

    setIsLoading(true);

    try {
      // 데이터 추출 및 가공

      // 주소 결합
      const fullAddress = form.detailAddress
        ? `${form.address} ${form.detailAddress}`
        : form.address;

      // 쿠키에서 referralSource, referralDetail 가져오기
      const referralSourceCode = Cookies.get("referralSource") || "";
      const referralDetailCode = Cookies.get("referralDetail") || "";

      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          referralSourceCode: referralSourceCode,
          referralDetailCode: referralDetailCode,
          name: form.name,
          email: form.email,
          address: fullAddress,
          birthDate: form.birth.replace(/-/g, ""), // YYYYMMDD 형식으로 변환
        }),
      });

      // 응답 처리
      if (!response.ok) {
        throw new Error("API 요청 실패");
      }

      // 주소 정보를 별도 쿠키에 저장 (공동 소유자 페이지에서 사용)
      Cookies.set(
        "representativeAddress",
        JSON.stringify({
          address: form.address.trim(),
          detailAddress: form.detailAddress.trim(),
        }),
        { expires: 7 }
      );

      // 계좌 정보를 쿠키에 저장 (NewsProofSubmission.js에서 사용)
      Cookies.set("bankCode", form.bank ? form.bank.label : "", { expires: 7 });
      Cookies.set("accountNumber", form.accountNumber.trim(), { expires: 7 });
      Cookies.set("accountHolder", form.accountHolder.trim(), { expires: 7 });

      // 동의 정보를 sessionStorage에 저장
      sessionStorage.setItem("consentData", JSON.stringify(consents));

      // 사용자 정보 쿠키에 저장
      Cookies.set("userData", JSON.stringify(form), { expires: 7 });

      // 신청 유형에 따라 다른 페이지로 리디렉션
      const applicationType = Cookies.get("applicationType") || "self";
      if (applicationType === "self") {
        // 본인 신청의 경우 스크린샷 페이지로 이동
        navigateTo(redirectPath || "/news-proof");
      } else {
        // 대표자 신청의 경우 공동 소유자 페이지로 이동
        navigateTo(redirectPath || "/co-owner-data");
      }
    } catch (error) {
      setApiError("데이터 저장 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 은행 선택 핸들러
  const handleBankChange = (selectedOption) => {
    setForm((prev) => ({ ...prev, bank: selectedOption }));
    if (errors.bank) {
      setErrors((prev) => ({ ...prev, bank: "" }));
    }
  };

  // 계좌번호 입력 핸들러
  const accountNumberRef = useRef(null);
  const accountHolderRef = useRef(null);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 영어 문자 확인 함수
  const containsEnglish = (text) => {
    return /[a-zA-Z]/.test(text);
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          {/* 추가: 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ToastIcon>!</ToastIcon>
                {toastMessage}
              </Toast>
            )}
          </AnimatePresence>

          <div>
            <Title>3. 기본 신원정보 확인</Title>
          </div>
          <Header>
            <StepIndicator>3 / 4</StepIndicator>
            <SubTitle>
              {step === 1
                ? isRepresentative
                  ? "대표자 정보 입력"
                  : "신원정보 입력"
                : step === 2
                ? "계좌정보 입력"
                : "유의사항"}
            </SubTitle>
            <Description>
              {step === 1
                ? isRepresentative
                  ? "소송 참여를 위한 대표자 정보를 입력해주세요"
                  : "소송 서류 작성에 필요한 정보를 입력해주세요"
                : step === 2
                ? "보상금 수령을 위한 계좌 정보를 입력해주세요"
                : "집단소송 참여를 위해 필요한 동의사항을 확인해주세요"}
            </Description>
            {step === 1 && (
              <InfoMessage>
                <InfoIcon>ⓘ</InfoIcon>
                <InfoTextContainer>
                  <>
                    <InfoTextBold>
                      본 신청은{" "}
                      <HighlightedText>미성년자와 외국인</HighlightedText>은
                      접수받지않습니다.
                    </InfoTextBold>
                  </>
                </InfoTextContainer>
              </InfoMessage>
            )}
          </Header>

          <AnimatePresence mode="wait">
            <FormContainer
              as={motion.div}
              key={step}
              variants={formContainerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {apiError && <ErrorAlert>{apiError}</ErrorAlert>}
              <SectionTitle>
                {step === 1
                  ? isRepresentative
                    ? "대표자 정보를 입력해주세요"
                    : "신원정보를 입력해주세요"
                  : step === 2
                  ? "계좌정보를 입력해주세요"
                  : "유의사항"}
              </SectionTitle>
              <AnimatePresence mode="wait" custom={direction}>
                <FormSection
                  as={motion.div}
                  key={step}
                  custom={direction}
                  variants={formContentVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  {step === 1 ? (
                    // 첫 번째 단계 - 기본 정보 입력
                    <>
                      <FormGroup>
                        <Label>
                          이름<Required>*</Required>
                        </Label>
                        <Input
                          type="text"
                          placeholder="이름을 입력하세요"
                          value={form.name}
                          onChange={(e) => {
                            // 영어 입력 확인
                            const value = e.target.value;
                            if (containsEnglish(value)) {
                              showToastMessage("외국인은 접수를 받지 않습니다");
                              // 영어 제외한 값만 설정
                              setForm((f) => ({
                                ...f,
                                name: value.replace(/[a-zA-Z]/g, ""),
                              }));
                            } else {
                              // 입력 중에는 모든 값을 허용 (영어 제외)
                              setForm((f) => ({ ...f, name: value }));
                            }
                          }}
                          ref={nameRef}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>
                          생년월일<Required>*</Required>
                        </Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={8}
                          placeholder="예시: 19990315"
                          value={form.birth}
                          onChange={handleChange("birth")}
                          ref={birthRef}
                          hasError={!!errors.birth}
                          isShaking={shakingFields.birth}
                        />
                        {errors.birth && (
                          <ErrorMessage>{errors.birth}</ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label>
                          이메일 주소<Required>*</Required>
                        </Label>
                        <Input
                          type="email"
                          placeholder="예시: example@gmail.com"
                          value={form.email}
                          onChange={handleChange("email")}
                          ref={emailRef}
                          hasError={!!errors.email}
                          isShaking={shakingFields.email}
                        />
                        {errors.email && (
                          <ErrorMessage>{errors.email}</ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label>
                          주소<Required>*</Required>
                        </Label>
                        <AddressInputContainer>
                          <AddressInput
                            type="text"
                            placeholder="주소 검색"
                            value={form.address}
                            readOnly
                            ref={addressRef}
                            onClick={openPostcodeSearch}
                          />
                        </AddressInputContainer>
                      </FormGroup>

                      <FormGroup>
                        <Label>상세주소</Label>
                        <Input
                          type="text"
                          placeholder="상세 주소를 입력해주세요"
                          value={form.detailAddress}
                          onChange={handleChange("detailAddress")}
                          ref={detailAddressRef}
                        />
                      </FormGroup>
                    </>
                  ) : step === 2 ? (
                    // 두 번째 단계 - 은행 정보 입력
                    <>
                      <InputContainer $isShaking={shakingFields.bank}>
                        <Label>
                          은행 선택<Required>*</Required>
                        </Label>
                        <StyledSelect
                          options={bankOptions}
                          value={form.bank}
                          onChange={handleBankChange}
                          placeholder="은행을 선택해주세요"
                          isSearchable={true}
                          classNamePrefix="select"
                          noOptionsMessage={() => "검색 결과가 없습니다"}
                          loadingMessage={() => "검색 중..."}
                          menuPortalTarget={document.body}
                          components={{
                            Option: CustomOption,
                            SingleValue: CustomSingleValue,
                          }}
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                        />
                        {errors.bank && <ErrorText>{errors.bank}</ErrorText>}
                      </InputContainer>

                      <InputContainer $isShaking={shakingFields.accountNumber}>
                        <Label>
                          계좌번호<Required>*</Required>
                        </Label>
                        <Input
                          ref={accountNumberRef}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form.accountNumber}
                          onChange={handleChange("accountNumber")}
                          placeholder="계좌번호를 입력해주세요"
                        />
                        {errors.accountNumber && (
                          <ErrorText>{errors.accountNumber}</ErrorText>
                        )}
                      </InputContainer>

                      <InputContainer $isShaking={shakingFields.accountHolder}>
                        <Label>
                          예금주명<Required>*</Required>
                        </Label>
                        <Input
                          ref={accountHolderRef}
                          type="text"
                          value={form.accountHolder}
                          onChange={(e) => {
                            setForm((f) => ({
                              ...f,
                              accountHolder: e.target.value,
                            }));
                          }}
                          onBlur={(e) => {
                            // 값이 비어있지 않은 경우에만 필터링 적용
                            if (e.target.value.trim()) {
                              let filteredName = e.target.value.trim();
                              if (filteredName !== form.name.trim()) {
                                setErrors((prev) => ({
                                  ...prev,
                                  accountHolder:
                                    "예금주명은 입력하신 이름과 동일해야 합니다.",
                                }));
                              }
                              setForm((f) => ({
                                ...f,
                                accountHolder: filteredName,
                              }));
                            }
                          }}
                          placeholder="예금주명을 입력해주세요"
                        />
                        {errors.accountHolder && (
                          <ErrorText>{errors.accountHolder}</ErrorText>
                        )}
                      </InputContainer>
                    </>
                  ) : (
                    // 세 번째 단계 - 유의사항 및 동의
                    <>
                      <ConsentSection>
                        <ConsentNotice>
                          <NoticeList>
                            <NoticeItem>
                              대표 신청자와 공동 신청자는{" "}
                              <NoticeHighlight>
                                2025년 4월 18일 <br />
                                이전에
                              </NoticeHighlight>{" "}
                              SKT 가입자 또는 SKT 알뜰폰 가입자이며,
                            </NoticeItem>
                            <NoticeItem>
                              허위 정보로 참여시 수임비용은{" "}
                              <NoticeHighlight>
                                환불되지 않습니다.
                              </NoticeHighlight>
                            </NoticeItem>
                            <NoticeItem>
                              추후 가입 증빙 서류 제출을 요청드릴 수 있습니다.
                              <br />
                              (요금 명세서, 가입내역 캡처 등)
                            </NoticeItem>
                          </NoticeList>
                          <ConsentCheckbox>
                            <CheckboxContainer
                              isShaking={shakingFields.victimConfirmation}
                              onClick={() =>
                                handleConsentChange("victimConfirmation")
                              }
                            >
                              <Checkbox
                                type="checkbox"
                                checked={consents.victimConfirmation}
                                onChange={() => {}}
                              />
                              <CheckboxLabel>
                                상기 내용을 모두 확인하였으며 동의합니다.
                              </CheckboxLabel>
                            </CheckboxContainer>
                            {errors.victimConfirmation && (
                              <ErrorMessage>
                                {errors.victimConfirmation}
                              </ErrorMessage>
                            )}
                          </ConsentCheckbox>
                        </ConsentNotice>
                      </ConsentSection>
                    </>
                  )}
                </FormSection>
              </AnimatePresence>
            </FormContainer>
          </AnimatePresence>

          <ButtonsContainer>
            {(step === 2 || step === 3) && (
              <PrevButton
                onClick={handlePrevStep}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                이전으로
              </PrevButton>
            )}
            <ActionButton
              onClick={
                step === 1
                  ? handleFirstStepComplete
                  : step === 2
                  ? handleSecondStepComplete
                  : handleSubmit
              }
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                (step === 1 && !isFirstStepFilled()) ||
                (step === 2 && !isSecondStepFilled()) ||
                (step === 3 && !isThirdStepFilled()) ||
                isLoading
              }
            >
              {isLoading ? "처리 중..." : step === 3 ? "작성완료" : "다음으로"}
            </ActionButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 추가 스타일
const AddressInputContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const AddressInput = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  cursor: pointer;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #0075ff;
    outline: none;
  }
`;

const SearchButton = styled.button`
  min-width: 60px;
  height: 50px;
  border-radius: 8px;
  background-color: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0066cc;
  }
`;

// 애니메이션 정의
const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  align-self: flex-start;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  margin-top: 5px;
  font-size: 16px;
  margin-right: 10px;
  font-weight: bold;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`;

const InfoTextBold = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;

const HighlightedText = styled.span`
  color: #dc4b3f;
  font-weight: 800;
`;

const InfoTextBold2 = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin-top: 5px;
`;

const InfoTextRegular = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 50px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 5px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const FormContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  border-radius: 12px;
  border: 1px solid rgba(0, 117, 255, 0.3);
  overflow: hidden;
  background-color: white;
  will-change: height;
`;

const FormSection = styled(motion.div)`
  width: 100%;
  padding: 20px;
  background-color: white;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  padding: 15px 20px;
  margin: 0;
  border-radius: 12px 12px 0px 0px;
  background: #eef4fe;
`;

const FormGroup = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2e3840;
`;

const Required = styled.span`
  color: #fe7156;
  margin-left: 2px;
`;

const Input = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? "#FF4D4F" : "#e5e5e5")};
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  animation: ${(props) => (props.isShaking ? shakeAnimation : "none")} 0.5s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: ${(props) => (props.hasError ? "#FF4D4F" : "#0075FF")};
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
`;

const ButtonsContainer = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

// 추가 스타일
const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

// 새로운 스타일 컴포넌트 추가
const StyledSelect = styled(Select)`
  .select__control {
    height: 50px;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: none;
    background: white;

    &:hover {
      border-color: #0075ff;
    }

    &--is-focused {
      border-color: #0075ff;
      box-shadow: 0 0 0 1px #0075ff;
    }
  }

  .select__placeholder {
    color: #999;
  }

  .select__single-value {
    color: #333;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .select__input {
    color: #333 !important;

    input {
      color: #333 !important;
    }
  }

  .select__input-container {
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #333;

    input {
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      border: none !important;
      position: absolute;
      color: #333 !important;

      &:focus {
        opacity: 1 !important;
        width: 100% !important;
        height: auto !important;
        position: relative;
      }
    }
  }

  .select__value-container {
    padding: 2px 16px;
    cursor: pointer;
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .select__menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 8px;
    z-index: 100;
    position: absolute;
    width: 100%;
    background: white;
  }

  .select__menu-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 8px 0;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;

      &:hover {
        background: #555;
      }
    }
  }

  .select__indicator-separator {
    display: none;
  }

  .select__clear-indicator {
    cursor: pointer;
    color: #999;

    &:hover {
      color: #666;
    }
  }

  .select__dropdown-indicator {
    color: #999;
    padding: 0 16px;

    &:hover {
      color: #666;
    }
  }
`;

// 스타일 컴포넌트 추가
const InputContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
  animation: ${(props) => (props.$isShaking ? shake : "none")} 0.5s;
`;

const ErrorText = styled.p`
  color: #ff4c4c;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
`;

// 이전 버튼 스타일 추가
const PrevButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #6495ed;
  background: #ffffff;
  color: #6495ed;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #efefef;
  }
`;

// 새로운 스타일 컴포넌트 추가
const OptionContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  min-height: 40px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const SingleValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  position: relative;
  top: 0;
`;

const BankIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

// 새로운 스타일 컴포넌트 추가
const Toast = styled(motion.div)`
  position: fixed;
  top: 230px;
  left: 5%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    max-width: 400px;
    min-width: 40%;
    left: 28.5%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 90%;
  max-width: 90%;
  text-align: left;
`;

const ToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #0075ff;
  color: white;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
`;

// 동의 관련 스타일 컴포넌트 추가
const ConsentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConsentNotice = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
`;

const NoticeTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const NoticeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
`;

const NoticeItem = styled.li`
  font-size: 13px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const NoticeHighlight = styled.span`
  color: #fe7156;
  font-weight: 600;
`;

const ConsentCheckbox = styled.div`
  margin-top: 16px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  animation: ${(props) => (props.isShaking ? shake : "none")} 0.5s;

  &:hover {
    background-color: rgba(0, 117, 255, 0.05);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #0075ff;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
  line-height: 1.5;
  flex: 1;
`;

export default UserDataCollection;
